import { createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL } from '../config'
import { getIDToken } from '../utils/AuthUtil'
import {
  setCurrentSubscriptionPlan,
  setFilePaymentStatus,
  setIntentClientSecret,
  setIsLoading,
  setIsPaymentProcessing,
  setPricingProduct,
  setSubscriptionInvoices,
  setSubscriptions,
  setSubscriptionsLoadingInProgress,
} from '../actions/payment'
import { RootState } from '../store'
import { showOneTimeServicePaymentDialog } from '../actions/organization'

const unixToDate = (unixTimeStamp: any) => {
  // Convert to milliseconds (JavaScript timestamps are in milliseconds)
  const date = new Date(unixTimeStamp * 1000)

  // Get a readable date and time
  const readableDate = date.toUTCString() // Format as UTC string
  console.log(readableDate)
  return readableDate
}

export const fetchSubscriptionsAsync = createAsyncThunk(
  'payment/fetchSubscriptions',
  async (_, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/subscriptions`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setSubscriptions(data))
        dispatch(setSubscriptionsLoadingInProgress(false))
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const fetchProductDetailsAsync = createAsyncThunk(
  'payment/fetchProductDetailsAsync',
  async (productName: string, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/fetchProduct`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        productName,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setPricingProduct(data))
        dispatch(setIsLoading(false))
      })
      .catch((error) =>
        console.error('Error during fetching pricing product: ', error),
      )
  },
)

export const createSetupIntentAsync = createAsyncThunk(
  'payment/createSetupIntent',
  async ({ email, name }: any, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/createSetupIntent`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        name,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setIntentClientSecret(data.client_secret))
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const makeFilePaymentAsync = createAsyncThunk(
  'payment/makeFilePayment',
  async (
    { priceId, fileId, productId, productName, amount, currency }: any,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const organizationId = state.organization.selectedOrganization?.id
    const response = await fetch(`${BASE_URL}/api/payment/fetchCustomer`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizationId,
      }),
    })
    const { customer_id } = await response.json()
    return fetch(`${BASE_URL}/api/payment/makePayment`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId: customer_id,
        priceId: priceId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        const { id, status } = data
        dispatch(
          updateFilePaymentStatusAsync({
            customerId: customer_id,
            priceId,
            fileId,
            paymentId: id,
            organizationId,
            productId,
            productName,
            status,
            amount,
            currency,
          }),
        )
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const createSubscriptionAsync = createAsyncThunk(
  'payment/createSubscription',
  async (
    { priceId, fileId, productId, productName }: any,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const organizationId = state.organization.selectedOrganization?.id
    const response = await fetch(`${BASE_URL}/api/payment/fetchCustomer`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizationId,
      }),
    })
    const { customer_id } = await response.json()
    return fetch(`${BASE_URL}/api/payment/createSubscription`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId: customer_id,
        priceId: priceId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        const {
          id,
          status,
          current_period_start,
          current_period_end,
          latest_invoice,
          plan,
        } = data
        const { payment_intent } = latest_invoice || {}
        dispatch(
          updateSubscriptionDetailsAsync({
            customerId: customer_id,
            priceId,
            fileId,
            paymentId: payment_intent?.id,
            organizationId,
            productId: plan.product || productId,
            productName,
            start_date: unixToDate(current_period_start),
            end_date: unixToDate(current_period_end),
            subscriptionId: id,
            status,
            interval: plan.interval,
          }),
        )
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const makeSubscriptionPaymentAsync = createAsyncThunk(
  'payment/makeSubscriptionPayment',
  async (
    { priceId, fileId, productId, productName, interval }: any,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const organizationId = state.organization.selectedOrganization?.id
    const response = await fetch(`${BASE_URL}/api/payment/fetchCustomer`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizationId,
      }),
    })
    const { customer_id } = await response.json()
    return fetch(`${BASE_URL}/api/payment/makePayment`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId: customer_id,
        priceId: priceId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        const { id, status } = data
        dispatch(
          updateSubscriptionPaymentStatusAsync({
            customerId: customer_id,
            priceId,
            fileId,
            paymentId: id,
            organizationId,
            productId,
            productName,
            status,
            interval,
          }),
        )
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const updateFilePaymentStatusAsync = createAsyncThunk(
  'payment/updateFilePaymentStatus',
  async (
    {
      customerId,
      priceId,
      fileId,
      paymentId,
      organizationId,
      productId,
      productName,
      status,
      amount,
      currency,
    }: any,
    { dispatch },
  ) => {
    return fetch(`${BASE_URL}/api/payment/updateFilePaymentStatus`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        priceId,
        fileId,
        paymentId,
        organizationId,
        productId,
        productName,
        status,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setIsPaymentProcessing(false))
        dispatch(showOneTimeServicePaymentDialog(false))
        dispatch(fetchPaymentStatusAsync({ fileId }))
        dispatch(
          updateOrganizationFilePaymentRequestAsync({
            fileId,
            organizationId,
            amount: amount,
            discipline: productName,
            currency: currency,
          }),
        )
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const updateOrganizationFilePaymentRequestAsync = createAsyncThunk(
  'payment/updateOrganizationFilePaymentRequest',
  async (
    { fileId, organizationId, amount, discipline, currency }: any,
    { dispatch },
  ) => {
    return fetch(
      `${BASE_URL}/api/payment/createOrganizationFilePaymentRequest`,
      {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId,
          organizationId,
          amount,
          discipline,
          currency,
        }),
      },
    )
      .then((response) => response.json())
      .then((data) => {})
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const updateSubscriptionPaymentStatusAsync = createAsyncThunk(
  'payment/updateSubscriptionPaymentStatus',
  async (
    {
      customerId,
      priceId,
      paymentId,
      organizationId,
      productId,
      productName,
      status,
      interval,
    }: any,
    { dispatch },
  ) => {
    return fetch(`${BASE_URL}/api/payment/updateSubscriptionPaymentStatus`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        priceId,
        paymentId,
        organizationId,
        productId,
        productName,
        status,
        interval,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setIsPaymentProcessing(false))
        dispatch(setCurrentSubscriptionPlan(data))
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const updateSubscriptionDetailsAsync = createAsyncThunk(
  'payment/updateSubscriptionDetails',
  async (
    {
      customerId,
      priceId,
      paymentId,
      organizationId,
      productId,
      productName,
      status,
      subscriptionId,
      interval,
    }: any,
    { dispatch },
  ) => {
    return fetch(`${BASE_URL}/api/payment/updateSubscriptionDetails`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        priceId,
        paymentId,
        organizationId,
        productId,
        productName,
        status,
        subscriptionId,
        interval,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setIsPaymentProcessing(false))
        dispatch(setCurrentSubscriptionPlan(data))
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const fetchPaymentStatusAsync = createAsyncThunk(
  'payment/fetchPaymentStatus',
  async ({ fileId }: any, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/fetchFilePaymentStatus`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(
          setFilePaymentStatus(
            data.map((payment: any) => payment.product_name),
          ),
        )
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const fetchSubscriptionInvoicesAsync = createAsyncThunk(
  'payment/fetchSubscriptionInvoices',
  async ({ subscriptionId }: any, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/fetchSubscriptionInvoices`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscriptionId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(setSubscriptionInvoices(data.data))
      })
      .catch((error) =>
        console.error('Error during fetching subscriptions: ', error),
      )
  },
)

export const updateCustomerDefaultPaymentMethodAsync = createAsyncThunk(
  'payment/updateCustomerDefaultPaymentMethod',
  async ({ customerId, paymentMethodId }: any, { dispatch }) => {
    return fetch(`${BASE_URL}/api/payment/updateCustomerDefaultPaymentMethod`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        paymentMethodId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {})
      .catch((error) =>
        console.error('Error during updating customer payment method: ', error),
      )
  },
)
